import React, { useState, useTransition, useEffect, useMemo } from 'react';
import { Typography, MenuItem, FormControl, Autocomplete, TextField, InputAdornment, Select, Box } from '@mui/material';
import { Controller } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { matchKorean, getChosung } from '../../../../utils/methods';
import { useQueryClient } from '@tanstack/react-query';
import FavoriteStar from '../../../common/FavoriteStar';
import { FAVORITE_TARGET } from '../../../../api/favorite';

import SellerShareSelect from '../../../common/SellerShareSelect';
import RegisterTypeToggle from './RegisterTypeToggle';
import TrainerSearchSelect from '../../../common/TrainerSearchSelect';
import { FormInputText } from '../../../../form-components/FormInputText';
import { FormInputComma } from '../../../../form-components/FormInputComma';
import { FormInputDate } from '../../../../form-components/FormInputDate';
import CustomRadioBox from '../../../CustomRadioBox';
import PointUsageFields from './PointUsageFields';

import {
  FormRow,
  FormLabel,
  FormInputWrapper,
  StyledFormControlLabel,
  SelectedMembersWrapper,
  SelectedMemberSpacer,
  MemberChip,
  SplitPaymentWrapper,
  SplitPaymentRow,
  SplitPaymentColumn,
  SplitPaymentInputRow,
  SplitPaymentFieldRow,
  SplitPaymentFieldLabel,
  SplitPaymentValidation,
  ValidationText,
  DropdownOptionWrapper,
  DropdownOptionLabel,
  DropdownStatusBadge,
  textInputStyle,
  disabledInputStyle,
  dateInputStyleFlex,
  autocompleteStyle,
  autocompleteInputStyle,
  splitPaymentInputStyle,
  splitOutstandingInputStyle,
  ErrorFieldWrapper,
  DateSeparator,
  ContentSection,
  FormFieldGroup,
  FormFieldGroupHalf,
  FormFieldRow,
  FormFieldLabel,
  BasePriceHint,
  ToggleRow,
  ToggleWrapper,
  ToggleText,
  CustomSwitch,
  ClassTypeFilterRow,
  classTypeSelectStyle,
} from './EnterSaleInfoModalStyled';

// 콤마 문자열/빈값을 숫자로. 나눠결제 합계 검증에서 반복 쓰인다.
const toAmount = value => Number(String(value ?? '').replace(/,/g, '')) || 0;

/**
 * 나눠결제 회원 1명 컬럼 — 결제금액과 미수금을 각각 입력받는다.
 * 미수금이 0이면 결제금액 행만 보여 화면이 불필요하게 길어지지 않게 한다.
 */
const SplitMemberAmountColumn = ({
  control,
  setValue,
  label,
  paymentFieldName,
  outstandingFieldName,
  showOutstanding,
  disabled = false,
  onPaymentChange,
  onOutstandingChange,
}) => {
  const { t } = useTranslation();

  const renderRow = (rowLabel, fieldName, inputStyle, onAmountChange) => (
    <SplitPaymentFieldRow>
      <SplitPaymentFieldLabel>{rowLabel}</SplitPaymentFieldLabel>
      <SplitPaymentInputRow>
        <FormInputComma
          control={control}
          name={fieldName}
          placeholder="0"
          disabled={disabled}
          sx={inputStyle}
          onAmountChange={value => {
            onAmountChange(value);
            setValue(fieldName, value);
          }}
        />
        <Typography sx={{ fontSize: '14px', minWidth: '20px' }}>{t('원')}</Typography>
      </SplitPaymentInputRow>
    </SplitPaymentFieldRow>
  );

  return (
    <SplitPaymentColumn>
      <FormFieldLabel>{label}</FormFieldLabel>
      {renderRow(t('결제금액'), paymentFieldName, splitPaymentInputStyle, onPaymentChange)}
      {showOutstanding && renderRow(t('미수금'), outstandingFieldName, splitOutstandingInputStyle, onOutstandingChange)}
    </SplitPaymentColumn>
  );
};

// InputAdornment 스타일
const adornmentStyle = { color: '#737373', fontSize: '13px', fontWeight: 500 };

const EnterSaleInfoModalTicketSection = ({
  control,
  errors,
  setValue,
  watch,
  branchManager,
  courseTicket,
  trainers,
  selectedTicketType,
  ticketPrice,
  ticketDiscountAmount,
  ticketFinalPrice,
  ticketOutstandingPrice,
  usageCount,
  ticketUsagePeriod,
  ticketUsagePeriodType,
  selectedMembers,
  setSelectedMembers,
  splitPayment,
  setSplitPayment,
  splitPaymentAmounts,
  setSplitPaymentAmounts,
  splitOutstandingAmounts,
  setSplitOutstandingAmounts,
  userDetail,
  searchResults,
  setSearchResults,
  setMemberId,
  debouncedGetMembers,
  handleChangeMember,
  getPrice,
  changeTicketPrice,
  ticketBasePrice,
  changeTicketFinalPrice,
  changeTicketOutstandingPrice,
  changeTicketPaidPrice,
  formatInteger,
  toast,
  errorFields,
  setErrorFields,
  showExpiredProducts,
  setShowExpiredProducts,
  isPointUsageEnabled,
  availablePoint,
  minUsePoint,
  usablePointLimit,
}) => {
  const { t } = useTranslation();
  const [isPending, startTransition] = useTransition();
  // 미수금이 있을 때만 나눠결제에 미수금 입력 행을 띄운다
  const hasSplitOutstanding = toAmount(ticketOutstandingPrice) > 0;
  const [classTypeFilter, setClassTypeFilter] = useState('ALL');
  const [sportTypeFilter, setSportTypeFilter] = useState('ALL');
  const [ticketPanelOpen, setTicketPanelOpen] = useState(false);
  const [ticketSearch, setTicketSearch] = useState('');
  const [trialFilter, setTrialFilter] = useState(false);

  // 종목 목록 추출
  const sportTypes = useMemo(() => {
    const types = new Set();
    courseTicket.forEach(t => (t.sportTypes || []).forEach(s => types.add(s)));
    return [...types];
  }, [courseTicket]);

  const queryClient = useQueryClient();

  const filteredCourseTicket = useMemo(() => {
    let filtered = courseTicket;
    if (!showExpiredProducts) {
      filtered = filtered.filter(ticket => ticket.status === 'ON_SALE' || ticket.status === 'APPROVED');
    }
    if (classTypeFilter !== 'ALL') {
      filtered = filtered.filter(ticket => ticket.classType === classTypeFilter);
    }
    if (sportTypeFilter !== 'ALL') {
      filtered = filtered.filter(ticket => (ticket.sportTypes || []).includes(sportTypeFilter));
    }
    if (trialFilter) {
      filtered = filtered.filter(ticket => ticket.ticketType === 'TRIAL_TICKET');
    }
    if (ticketSearch.trim()) {
      filtered = filtered.filter(ticket => matchKorean(ticket.label || '', ticketSearch.trim()));
    }
    // 즐겨찾기 → 판매중 먼저 + ㄱㄴㄷ → 영어 → 숫자 → 특문
    const charOrder = c => {
      if (/[가-힣ㄱ-ㅎㅏ-ㅣ]/.test(c)) return 0;
      if (/[a-zA-Z]/.test(c)) return 1;
      if (/[0-9]/.test(c)) return 2;
      return 3;
    };
    filtered.sort((a, b) => {
      if (!!a.favorite !== !!b.favorite) return a.favorite ? -1 : 1;
      const aActive = a.status === 'ON_SALE' || a.status === 'APPROVED';
      const bActive = b.status === 'ON_SALE' || b.status === 'APPROVED';
      if (aActive !== bActive) return bActive - aActive;
      const aFirst = (a.label || '')[0] || '';
      const bFirst = (b.label || '')[0] || '';
      const orderDiff = charOrder(aFirst) - charOrder(bFirst);
      if (orderDiff !== 0) return orderDiff;
      return (a.label || '').localeCompare(b.label || '', 'ko');
    });
    return filtered;
  }, [courseTicket, classTypeFilter, sportTypeFilter, trialFilter, showExpiredProducts, ticketSearch]);

  // 시작일 변경 시 종료일 자동 계산
  const watchedStartDate = watch('courseStartDate');

  useEffect(() => {
    if (!watchedStartDate || !ticketUsagePeriod || !ticketUsagePeriodType) return;

    let endDate;
    if (ticketUsagePeriodType === 'MONTH') {
      endDate = dayjs(watchedStartDate).add(ticketUsagePeriod, 'month').subtract(1, 'day').toDate();
    } else if (ticketUsagePeriodType === 'DAY') {
      endDate = dayjs(watchedStartDate).add(ticketUsagePeriod, 'day').subtract(1, 'day').toDate();
    }

    if (endDate) {
      setValue('courseEndDate', endDate);
    }
  }, [watchedStartDate, ticketUsagePeriod, ticketUsagePeriodType, setValue]);

  return (
    <ContentSection>
      {/* #124 신규등록/재등록 — 라디오 카드. 기본값은 서버 자동 판정, 바꾸면 그 값이 저장된다 */}
      <RegisterTypeToggle control={control} setValue={setValue} />

      {/* 판매일시 + 판매 담당자 */}
      <FormFieldRow>
        <FormFieldGroupHalf>
          <FormFieldLabel>{t('판매일시')}</FormFieldLabel>
          <FormInputDate
            name="saleDate"
            control={control}
            placeholder={t('판매일(오늘)')}
            renderInput={params => (
              <TextField color="info" size="small" variant="outlined" {...params} sx={{ ...dateInputStyleFlex, width: '100%' }} />
            )}
          />
        </FormFieldGroupHalf>
        <FormFieldGroupHalf>
          <FormFieldLabel>{t('판매 담당자')}</FormFieldLabel>
          <ErrorFieldWrapper hasError={errorFields?.branchManager}>
            {/* #122 판매 담당자 N명 + 귀속 비율 — 담당 강사 다중 선택과 같은 구성 */}
            <SellerShareSelect
              control={control}
              setValue={setValue}
              name="branchManager"
              sharesName="sellerShares"
              options={branchManager}
              placeholder={t('담당자 선택')}
              onChange={() => setErrorFields(prev => ({ ...prev, branchManager: false }))}
            />
          </ErrorFieldWrapper>
        </FormFieldGroupHalf>
      </FormFieldRow>

      {/* 수강권 선택 with toggle */}
      <FormFieldGroup>
        <ToggleRow>
          <FormFieldLabel>{t('수강권 선택')}</FormFieldLabel>
          <ToggleWrapper>
            <ToggleText>{t('판매종료 상품 노출')}</ToggleText>
            <CustomSwitch checked={showExpiredProducts} onChange={e => setShowExpiredProducts(e.target.checked)} />
          </ToggleWrapper>
        </ToggleRow>
        <ErrorFieldWrapper hasError={errorFields?.courseTicket}>
          <Box sx={{ width: '100%' }}>
            {/* 헤더 (클릭하면 펼침/접힘) */}
            <Box
              onClick={() => { setTicketPanelOpen(prev => !prev); setTicketSearch(''); }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: '44px',
                px: '14px',
                border: ticketPanelOpen ? '1.5px solid #0F7F85' : '1px solid #c4c4c4',
                borderRadius: ticketPanelOpen ? '6px 6px 0 0' : '6px',
                cursor: 'pointer',
                backgroundColor: watch('courseTicket') ? '#f0f8f8' : '#fff',
                transition: 'all 0.15s ease',
                '&:hover': { borderColor: '#0F7F85' },
              }}>
              <Typography sx={{ fontSize: '14px', fontWeight: watch('courseTicket') ? 500 : 400, color: watch('courseTicket') ? '#0F7F85' : '#999' }}>
                {watch('courseTicket')
                  ? filteredCourseTicket.find(t => t.value === watch('courseTicket'))?.label || courseTicket.find(t => t.value === watch('courseTicket'))?.label || '선택됨'
                  : '수강권을 선택해 주세요'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', borderRadius: '50%', backgroundColor: ticketPanelOpen ? '#0F7F85' : 'transparent', transition: 'all 0.15s ease' }}>
                {ticketPanelOpen ? <KeyboardArrowUpIcon sx={{ color: '#fff', fontSize: 18 }} /> : <KeyboardArrowDownIcon sx={{ color: '#999', fontSize: 18 }} />}
              </Box>
            </Box>

            {/* 패널 (펼쳐졌을 때) */}
            {ticketPanelOpen && (
              <Box sx={{ border: '1.5px solid #0F7F85', borderTop: 'none', borderRadius: '0 0 6px 6px', maxHeight: '500px', display: 'flex', flexDirection: 'column' }}>
                {/* 검색 + 클래스 필터 */}
                <Box sx={{ display: 'flex', gap: '8px', p: '10px 12px', borderBottom: '1px solid #eee' }}>
                  <Select
                    value={classTypeFilter}
                    onChange={e => { setClassTypeFilter(e.target.value); setValue('courseTicket', ''); }}
                    size="small"
                    sx={{ minWidth: '100px', fontSize: '13px', '& .MuiSelect-select': { py: '6px' } }}>
                    <MenuItem value="ALL">{t('전체')}</MenuItem>
                    <MenuItem value="PRIVATE_LESSON">{t('개인')}</MenuItem>
                    <MenuItem value="GROUP_LESSON">{t('그룹')}</MenuItem>
                    <MenuItem value="DUET_LESSON">{t('듀엣')}</MenuItem>
                    <MenuItem value="OT">OT</MenuItem>
                  </Select>
                  <TextField
                    size="small"
                    placeholder="수강권 검색"
                    value={ticketSearch}
                    onChange={e => setTicketSearch(e.target.value)}
                    onClick={e => e.stopPropagation()}
                    autoFocus
                    sx={{ flex: 1, '& .MuiOutlinedInput-root': { height: '36px', fontSize: '13px' } }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#999' }} /></InputAdornment> }}
                  />
                </Box>

                {/* 종목 + 일일권 필터 */}
                {(sportTypes.length > 1 || courseTicket.some(t => t.ticketType === 'TRIAL_TICKET')) && (
                  <Box sx={{ display: 'flex', gap: '6px', px: '12px', py: '8px', borderBottom: '1px solid #eee', flexWrap: 'wrap' }}>
                    {sportTypes.length > 1 && (
                      <>
                        <Box
                          onClick={() => setSportTypeFilter('ALL')}
                          sx={{
                            px: '10px', py: '4px', borderRadius: '14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                            backgroundColor: sportTypeFilter === 'ALL' ? '#4ab3bc' : '#f5f5f5',
                            color: sportTypeFilter === 'ALL' ? '#fff' : '#666',
                            '&:hover': { opacity: 0.8 },
                          }}>
                          전체
                        </Box>
                        {sportTypes.map(type => (
                          <Box
                            key={type}
                            onClick={() => setSportTypeFilter(type)}
                            sx={{
                              px: '10px', py: '4px', borderRadius: '14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                              backgroundColor: sportTypeFilter === type ? '#4ab3bc' : '#f5f5f5',
                              color: sportTypeFilter === type ? '#fff' : '#666',
                              '&:hover': { opacity: 0.8 },
                            }}>
                            {t(type)}
                          </Box>
                        ))}
                        {courseTicket.some(t => t.ticketType === 'TRIAL_TICKET') && (
                          <Box sx={{ width: '1px', height: '24px', backgroundColor: '#ddd', mx: '2px' }} />
                        )}
                      </>
                    )}
                    {courseTicket.some(t => t.ticketType === 'TRIAL_TICKET') && (
                      <Box
                        onClick={() => setTrialFilter(prev => !prev)}
                        sx={{
                          px: '10px', py: '4px', borderRadius: '14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                          backgroundColor: trialFilter ? '#e67e22' : '#f5f5f5',
                          color: trialFilter ? '#fff' : '#666',
                          '&:hover': { opacity: 0.8 },
                        }}>
                        일일권
                      </Box>
                    )}
                  </Box>
                )}

                {/* 수강권 리스트 */}
                <Box sx={{ overflowY: 'auto', flex: 1 }}>
                  {filteredCourseTicket.length === 0 ? (
                    <Box sx={{ p: '24px', textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '13px', color: '#999' }}>검색 결과가 없습니다</Typography>
                    </Box>
                  ) : (
                    (() => {
                      let lastCho = '';
                      return filteredCourseTicket.map(ticket => {
                        const firstChar = (ticket.label || '')[0] || '';
                        // 즐겨찾기는 초성 대신 '즐겨찾기' 그룹으로 묶어 최상단에 보인다
                        const cho = ticket.favorite ? '★ 즐겨찾기' : getChosung(firstChar);
                        const showHeader = cho !== lastCho;
                        lastCho = cho;
                        const isSelected = watch('courseTicket') === ticket.value;
                        const isEnded = ticket.status !== 'ON_SALE' && ticket.status !== 'APPROVED';
                      return (
                        <React.Fragment key={ticket.value}>
                        {showHeader && (
                          <Box sx={{ px: '14px', py: '4px', backgroundColor: '#f7f7f7', borderBottom: '1px solid #eee' }}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#999' }}>{cho}</Typography>
                          </Box>
                        )}
                        <Box
                          onClick={() => {
                            setValue('courseTicket', ticket.value);
                            getPrice(ticket.value);
                            setErrorFields(prev => ({ ...prev, courseTicket: false }));
                            setTicketPanelOpen(false);
                            setTicketSearch('');
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: '14px',
                            py: '10px',
                            cursor: 'pointer',
                            backgroundColor: isSelected ? '#f0f8f8' : isEnded ? '#fafafa' : '#fff',
                            borderBottom: '1px solid #f5f5f5',
                            '&:hover': { backgroundColor: isSelected ? '#e6f5f5' : '#f5f5f5' },
                            '&:last-child': { borderBottom: 'none' },
                          }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
                            {/* 판매등록 전용 즐겨찾기(SALE_*) — 토글 후 옵션 목록을 다시 불러 그룹을 재정렬한다 */}
                            <FavoriteStar
                              targetType={ticket.ticketType === 'TRIAL_TICKET' ? FAVORITE_TARGET.SALE_TRIAL : FAVORITE_TARGET.SALE_TICKET}
                              targetId={ticket.value}
                              favorite={ticket.favorite}
                              onChanged={() => queryClient.invalidateQueries({ queryKey: ['courseTicket'] })}
                            />
                            {isSelected && <CheckCircleIcon sx={{ fontSize: 18, color: '#0F7F85', flexShrink: 0 }} />}
                            <Typography sx={{ fontSize: '13px', color: isEnded ? '#bbb' : '#333', fontWeight: isSelected ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {ticket.name || ticket.label}
                            </Typography>
                            {ticket.sub && (
                              <Typography sx={{ fontSize: '12px', color: '#999', flexShrink: 0 }}>
                                {ticket.sub}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px', flexShrink: 0, ml: '8px' }}>
                            <Box sx={{ display: 'flex', gap: '4px' }}>
                              {ticket.ticketType === 'TRIAL_TICKET' && (
                                <Typography sx={{ fontSize: '10px', fontWeight: 600, px: '6px', py: '1px', borderRadius: '3px', color: '#e67e22', backgroundColor: '#fef5e7', border: '1px solid #f5d9a8' }}>
                                  일일권
                                </Typography>
                              )}
                              {(ticket.sportTypes || []).map(st => (
                                <Typography key={st} sx={{ fontSize: '10px', fontWeight: 600, px: '6px', py: '1px', borderRadius: '3px', color: '#4ab3bc', backgroundColor: '#edf8f9', border: '1px solid #d0edf0' }}>
                                  {t(st)}
                                </Typography>
                              ))}
                            </Box>
                            {/* 운동 라벨 아래 상품 금액 — 할인이 있으면 정가에 취소선을 긋고 판매가를 함께 보여준다. */}
                            {ticket.regularPrice > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                {ticket.finalPrice != null && ticket.finalPrice !== ticket.regularPrice && (
                                  <Typography sx={{ fontSize: '11px', lineHeight: 1, color: '#bbb', textDecoration: 'line-through' }}>
                                    {formatInteger(ticket.regularPrice)}원
                                  </Typography>
                                )}
                                <Typography sx={{ fontSize: '12px', lineHeight: 1, fontWeight: 600, color: isEnded ? '#bbb' : '#0F7F85' }}>
                                  {formatInteger(ticket.finalPrice ?? ticket.regularPrice)}원
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                        </React.Fragment>
                      );
                    });
                    })()
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </ErrorFieldWrapper>
      </FormFieldGroup>

      {/* PRIVATE_LESSON·OT 일 때 담당 강사 선택 (#77 다중 배정 — 전원 동등).
          OT 는 개인레슨 규칙을 따르므로 후보 목록도 개인레슨 담당 강사와 같다. */}
      {(selectedTicketType === 'PRIVATE_LESSON' || selectedTicketType === 'OT') && (
        <FormFieldGroup>
          <FormFieldLabel>{t('담당 강사')}</FormFieldLabel>
          <TrainerSearchSelect control={control} name="trainers" options={trainers} selectAll />
        </FormFieldGroup>
      )}

      {/* 판매금액 / 할인금액 (2 columns) */}
      <FormFieldRow>
        <FormFieldGroupHalf>
          {/* 상품을 골라야 원가가 정해진다. 미선택 상태의 입력은 상품 선택 시 덮어써지므로 잠근다. */}
          <FormFieldLabel>
            {t('판매금액')}
            {/* 원가와 다르게 팔면 기준 금액을 라벨 옆에 같이 보여준다 */}
            {ticketBasePrice > 0 && formatInteger(ticketPrice) !== formatInteger(ticketBasePrice) && (
              <BasePriceHint>정가 {formatInteger(ticketBasePrice)}원</BasePriceHint>
            )}
          </FormFieldLabel>
          <FormInputText
            control={control}
            onChange={e => changeTicketPrice(e.target.value)}
            disabled={!watch('courseTicket')}
            color="info"
            sx={!watch('courseTicket') ? disabledInputStyle : textInputStyle}
            variant="outlined"
            name="ticketPrice"
            size="small"
            staticValue={formatInteger(ticketPrice)}
            placeholder={t('0')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={adornmentStyle}>
                  {t('원')}
                </InputAdornment>
              ),
            }}
          />
        </FormFieldGroupHalf>
        <FormFieldGroupHalf>
          <FormFieldLabel>{t('할인금액')}</FormFieldLabel>
          <FormInputText
            control={control}
            onChange={e => changeTicketFinalPrice(e.target.value)}
            color="info"
            sx={textInputStyle}
            variant="outlined"
            name="ticketDiscountAmount"
            size="small"
            value={ticketDiscountAmount}
            placeholder={t('0')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={adornmentStyle}>
                  {t('원')}
                </InputAdornment>
              ),
            }}
          />
        </FormFieldGroupHalf>
      </FormFieldRow>

      {/* 보유 포인트 / 사용할 포인트 (정식회원 + 지점 포인트 사용 설정 on 일 때만, 듀엣레슨은 그룹결제라 범위 제외) */}
      <PointUsageFields
        isVisible={isPointUsageEnabled && selectedTicketType !== 'DUET_LESSON'}
        control={control}
        availablePoint={availablePoint}
        minUsePoint={minUsePoint}
        usablePointLimit={usablePointLimit}
        errorFields={errorFields}
        setErrorFields={setErrorFields}
        formatInteger={formatInteger}
      />

      {/* 미수금 / 결제금액 (2 columns) */}
      <FormFieldRow>
        <FormFieldGroupHalf>
          <FormFieldLabel>{t('미수금')}</FormFieldLabel>
          <FormInputText
            control={control}
            onChange={e => changeTicketOutstandingPrice(e.target.value)}
            color="info"
            sx={{
              ...textInputStyle,
              '& .MuiOutlinedInput-input': {
                ...textInputStyle['& .MuiOutlinedInput-input'],
                color: '#ee735a !important',
              },
            }}
            variant="outlined"
            name="ticketOutstandingPrice"
            size="small"
            value={formatInteger(ticketOutstandingPrice)}
            placeholder={t('0')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={adornmentStyle}>
                  {t('원')}
                </InputAdornment>
              ),
            }}
          />
        </FormFieldGroupHalf>
        <FormFieldGroupHalf>
          <FormFieldLabel>{t('결제금액')}</FormFieldLabel>
          <FormInputText
            control={control}
            onChange={e => changeTicketPaidPrice(e.target.value)}
            color="info"
            sx={textInputStyle}
            variant="outlined"
            name="ticketFinalPrice"
            size="small"
            value={formatInteger(ticketFinalPrice)}
            placeholder={t('0')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={adornmentStyle}>
                  {t('원')}
                </InputAdornment>
              ),
            }}
          />
        </FormFieldGroupHalf>
      </FormFieldRow>

      {/* 이용기간 */}
      <FormFieldGroup>
        <FormFieldLabel>{t('이용기간')}</FormFieldLabel>
        <FormInputWrapper>
          <ErrorFieldWrapper hasError={errorFields?.courseStartDate}>
            <FormInputDate
              name="courseStartDate"
              control={control}
              rules={{ required: true }}
              placeholder={t('오늘 날짜')}
              onChange={() => setErrorFields(prev => ({ ...prev, courseStartDate: false }))}
              renderInput={params => (
                <TextField color="info" size="small" variant="outlined" {...params} sx={dateInputStyleFlex} />
              )}
            />
          </ErrorFieldWrapper>
          <DateSeparator>~</DateSeparator>
          <ErrorFieldWrapper hasError={errorFields?.courseEndDate}>
            <FormInputDate
              name="courseEndDate"
              control={control}
              placeholder={t('자동')}
              onChange={() => setErrorFields(prev => ({ ...prev, courseEndDate: false }))}
              renderInput={params => (
                <TextField color="info" size="small" variant="outlined" {...params} sx={dateInputStyleFlex} />
              )}
            />
          </ErrorFieldWrapper>
        </FormInputWrapper>
      </FormFieldGroup>

      {/* 이용횟수 */}
      <FormFieldGroup>
        <FormFieldLabel>{t('이용횟수')}</FormFieldLabel>
        <FormInputText
          control={control}
          color="info"
          sx={textInputStyle}
          variant="outlined"
          name="usageCount"
          size="small"
          value={usageCount}
          placeholder={t('이용횟수')}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={adornmentStyle}>
                {t('회')}
              </InputAdornment>
            ),
          }}
        />
      </FormFieldGroup>

      {/* DUET_LESSON 일 때 회원 선택 */}
      {selectedTicketType === 'DUET_LESSON' && (
        <FormFieldGroup>
          <FormFieldLabel>{t('듀엣 회원 선택')}</FormFieldLabel>
          <Controller
            name="memberId"
            control={control}
            rules={{ required: true }}
            placeholder={t("Type the client's name")}
            render={({ field }) => (
              <FormControl
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    ...(errors.memberId && {
                      '& fieldset': {
                        borderColor: '#dc2626',
                        borderWidth: '2px',
                      },
                    }),
                  },
                }}
                error={!!errors.memberId}>
                <Autocomplete
                  sx={autocompleteStyle}
                  componentsProps={{
                    popper: {
                      placement: 'bottom-start',
                      modifiers: [{ name: 'flip', enabled: false }],
                    },
                  }}
                  onChange={(e, v) => {
                    if (v) {
                      const isAlreadyAdded = selectedMembers.some(member => member.id === v.id);
                      if (isAlreadyAdded) {
                        toast.error('이미 추가된 회원입니다');
                        return;
                      }
                      if (userDetail && userDetail.memberId === v.id) {
                        toast.error('이미 선택된 회원입니다');
                        return;
                      }
                      const currentTotalCount = (userDetail ? 1 : 0) + selectedMembers.length;
                      const maxTotalMembers = 2;
                      if (currentTotalCount >= maxTotalMembers) {
                        toast.error('듀엣레슨은 최대 2명까지만 선택 가능합니다');
                        return;
                      }

                      const nameOnly = v.name.split(' (')[0].replace(/[0-9]/g, '');
                      setSelectedMembers(prev => [...prev, { id: v.id, name: nameOnly }]);
                      field.onChange(v.id);
                      setValue('memberId', v.id);
                      setMemberId(v.id);

                      startTransition(() => {
                        setValue('searchInput', '');
                        setSearchResults([]);
                      });
                      handleChangeMember(v.id);
                    } else {
                      field.onChange(null);
                      setValue('memberId', null);
                      setMemberId(null);
                      setSelectedMembers([]);
                      setSearchResults([]);
                    }
                  }}
                  onInputChange={(e, newInputValue, reason) => {
                    if (reason === 'input') {
                      debouncedGetMembers(newInputValue);
                      if (newInputValue !== (searchResults.find(option => option.id === field.value)?.name || '')) {
                        field.onChange(null);
                        setValue('memberId', null);
                        setMemberId(null);
                      }
                    }
                  }}
                  value={searchResults.find(option => option.id === field.value) || null}
                  disablePortal
                  options={searchResults || []}
                  getOptionLabel={option => option?.name}
                  noOptionsText="검색결과가 없습니다"
                  filterOptions={x => x}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.name}
                    </li>
                  )}
                  blurOnSelect={true}
                  clearOnBlur={true}
                  openOnFocus={true}
                  popupIcon={<SearchIcon sx={{ color: '#9a9a9a' }} />}
                  renderInput={params => (
                    <TextField
                      {...params}
                      placeholder="회원명 또는 전화번호 검색"
                      name="searchInput"
                      label=""
                      sx={autocompleteInputStyle}
                    />
                  )}
                />
              </FormControl>
            )}
          />
        </FormFieldGroup>
      )}

      {/* 선택된 회원 표시 */}
      {((userDetail && selectedTicketType === 'PRIVATE_LESSON') || selectedTicketType === 'DUET_LESSON') && (
        <SelectedMembersWrapper style={{ paddingLeft: 0 }}>
          <SelectedMemberSpacer />
          {userDetail && selectedTicketType === 'DUET_LESSON' && (
            <MemberChip key={userDetail.memberId} component="span">
              {userDetail.name.split(' (')[0].replace(/[0-9]/g, '')}
              <CloseIcon
                sx={{ fontSize: '16px', cursor: 'pointer', '&:hover': { opacity: 0.7 } }}
                onClick={() => toast.error('기본으로 선택된 인원은 삭제할 수 없습니다.')}
              />
            </MemberChip>
          )}
          {selectedMembers.map(user => (
            <MemberChip key={user.id} component="span">
              {user.name}
              <CloseIcon
                sx={{ fontSize: '16px', cursor: 'pointer', '&:hover': { opacity: 0.7 } }}
                onClick={() => {
                  setSelectedMembers(prev => prev.filter(u => u.id !== user.id));
                  setValue('memberId', null);
                  setMemberId(null);
                }}
              />
            </MemberChip>
          ))}
        </SelectedMembersWrapper>
      )}

      {/* 나눠 결제 */}
      {selectedTicketType === 'DUET_LESSON' && (
        <FormFieldGroup>
          <FormFieldLabel>{t('나눠 결제')}</FormFieldLabel>
          <FormInputWrapper style={{ gap: '20px', justifyContent: 'flex-start' }}>
            <StyledFormControlLabel
              control={
                <CustomRadioBox
                  checked={splitPayment === false}
                  onChange={() => {
                    setSplitPayment(false);
                    setSplitPaymentAmounts({ userDetail: '', selectedMember: '' });
                    setSplitOutstandingAmounts({ userDetail: '', selectedMember: '' });
                    setValue('splitPayment_userDetail', '');
                    setValue('splitPayment_selectedMember_0', '');
                    setValue('splitOutstanding_userDetail', '');
                    setValue('splitOutstanding_selectedMember_0', '');
                  }}
                />
              }
              label={t('미사용')}
            />
            <StyledFormControlLabel
              control={<CustomRadioBox checked={splitPayment === true} onChange={() => setSplitPayment(true)} />}
              label={t('사용')}
            />
          </FormInputWrapper>
        </FormFieldGroup>
      )}

      {/* 나눠 결제 금액 입력 — 회원별 결제금액 + 미수금 */}
      {splitPayment === true && (
        <SplitPaymentWrapper style={{ paddingLeft: 0 }}>
          <SplitPaymentRow>
            {userDetail && selectedTicketType === 'DUET_LESSON' && (
              <SplitMemberAmountColumn
                control={control}
                setValue={setValue}
                label={
                  <>
                    <strong>{userDetail.name.split(' (')[0].replace(/[0-9]/g, '')}</strong> 님
                  </>
                }
                paymentFieldName="splitPayment_userDetail"
                outstandingFieldName="splitOutstanding_userDetail"
                showOutstanding={hasSplitOutstanding}
                onPaymentChange={value => setSplitPaymentAmounts(prev => ({ ...prev, userDetail: value }))}
                onOutstandingChange={value => setSplitOutstandingAmounts(prev => ({ ...prev, userDetail: value }))}
              />
            )}
            {selectedTicketType === 'DUET_LESSON' && (
              <SplitMemberAmountColumn
                control={control}
                setValue={setValue}
                label={
                  selectedMembers.length > 0 ? (
                    <>
                      <strong>{selectedMembers[0].name}</strong> 님
                    </>
                  ) : (
                    <Typography sx={{ color: '#9a9a9a' }}>회원을 선택해 주세요</Typography>
                  )
                }
                paymentFieldName="splitPayment_selectedMember_0"
                outstandingFieldName="splitOutstanding_selectedMember_0"
                showOutstanding={hasSplitOutstanding}
                disabled={selectedMembers.length === 0}
                onPaymentChange={value => setSplitPaymentAmounts(prev => ({ ...prev, selectedMember: value }))}
                onOutstandingChange={value => setSplitOutstandingAmounts(prev => ({ ...prev, selectedMember: value }))}
              />
            )}
          </SplitPaymentRow>

          {/* 합계 검증 메시지 — 결제금액·미수금 각각 전체 입력값과 맞아야 한다 */}
          {(() => {
            const paidTotal = toAmount(ticketFinalPrice);
            const paidSum = toAmount(splitPaymentAmounts.userDetail) + toAmount(splitPaymentAmounts.selectedMember);
            const outstandingTotal = toAmount(ticketOutstandingPrice);
            const outstandingSum =
              toAmount(splitOutstandingAmounts.userDetail) + toAmount(splitOutstandingAmounts.selectedMember);

            const rows = [];
            if (paidTotal > 0 || paidSum > 0) {
              rows.push({ key: 'paid', label: t('결제금액'), sum: paidSum, total: paidTotal });
            }
            if (outstandingTotal > 0 || outstandingSum > 0) {
              rows.push({ key: 'outstanding', label: t('미수금'), sum: outstandingSum, total: outstandingTotal });
            }
            if (rows.length === 0) {
              return null;
            }

            return (
              <SplitPaymentValidation sx={{ flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                {rows.map(row => (
                  <ValidationText key={row.key} isMatch={row.sum === row.total}>
                    {row.label} 합계: {formatInteger(row.sum)}원 / {row.label}: {formatInteger(row.total)}원{' '}
                    {row.sum === row.total ? '✓' : `(차이: ${formatInteger(Math.abs(row.total - row.sum))}원)`}
                  </ValidationText>
                ))}
              </SplitPaymentValidation>
            );
          })()}
        </SplitPaymentWrapper>
      )}
    </ContentSection>
  );
};

export default EnterSaleInfoModalTicketSection;
